'use client'

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react'

// Контекст для управления мобильным сайдбаром
interface MobileSidebarContextType {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  isMobile: boolean
  // Какая панель активна на мобильных: 'sidebar' или 'content'
  mobilePanel: 'sidebar' | 'content'
  setMobilePanel: (panel: 'sidebar' | 'content') => void
  // Обработчик свайпа
  swipeHandlers: {
    onTouchStart: (e: React.TouchEvent) => void
    onTouchMove: (e: React.TouchEvent) => void
    onTouchEnd: (e: React.TouchEvent) => void
  }
}

const MobileSidebarContext = createContext<MobileSidebarContextType | undefined>(undefined)

// Брейкпоинт для мобильных устройств
const MOBILE_BREAKPOINT = 640

// Порог свайпа в пикселях
const SWIPE_THRESHOLD = 50

export function MobileSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [mobilePanel, setMobilePanel] = useState<'sidebar' | 'content'>('content')

  // Рефы для отслеживания свайпа
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const touchCurrentX = useRef<number | null>(null)

  // Отслеживаем размер экрана
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)
      // Закрываем сайдбар при переходе на десктоп
      if (!mobile) {
        setIsOpen(false)
        setMobilePanel('content')
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Блокируем скролл body при открытом сайдбаре (только для drawer-режима)
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, isMobile])

  const open = useCallback(() => {
    setIsOpen(true)
    setMobilePanel('sidebar')
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setMobilePanel('content')
  }, [])

  const toggle = useCallback(() => {
    if (mobilePanel === 'sidebar') {
      close()
    } else {
      open()
    }
  }, [mobilePanel, open, close])

  // Обработчики свайпа — отключены, переход осуществляется по кнопке
  const onTouchStart = useCallback((_e: React.TouchEvent) => {
    // no-op
  }, [])

  const onTouchMove = useCallback((_e: React.TouchEvent) => {
    // no-op
  }, [])

  const onTouchEnd = useCallback((_e: React.TouchEvent) => {
    // no-op — свайп отключён, переход по кнопке
  }, [])

  // Стабильный объект swipeHandlers — не создаётся заново при каждом рендере
  const swipeHandlers = useMemo(() => ({
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  }), [onTouchStart, onTouchMove, onTouchEnd])

  // Стабилизируем значение контекста через useMemo
  const contextValue = useMemo(() => ({
    isOpen, open, close, toggle, isMobile, mobilePanel, setMobilePanel, swipeHandlers
  }), [isOpen, open, close, toggle, isMobile, mobilePanel, swipeHandlers])

  return (
    <MobileSidebarContext.Provider value={contextValue}>
      {children}
    </MobileSidebarContext.Provider>
  )
}

// Хук для использования контекста мобильного сайдбара
export function useMobileSidebar() {
  const context = useContext(MobileSidebarContext)
  if (context === undefined) {
    throw new Error('useMobileSidebar must be used within a MobileSidebarProvider')
  }
  return context
}
